import React, { Component } from 'react';
import {
  TextField,
  Tabs,
  FormControlLabel,
  Checkbox,
  Typography,
  Tab,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import _ from 'lodash';

import { createArray } from '../../utilities/arrayHelper';
import Scorecard from '../../core/scorecard';
import GroundTruthOneOf from './GroundTruthOneOf';
import GroundTruthArbitraryString from './GroundTruthArbitraryString';
import GroundTruthMultipleChoice from './GroundTruthMultipleChoice';
import { LATEST_GT_VERSION } from '../../environment';
import {
  GroundTruth,
  Survey,
  SurveyQuestion,
  SurveySection,
} from '../../redux/types';

type Props = {
  survey: Survey;
  aggregation: any;
  groundTruth?: GroundTruth;
  disabled: boolean;
}

type StyleType = {
  backgroundColor: string;
  color: string;
  borderBottom?: string
  border?: string;
}

type State = {
  minimumScore: number;
  currentSection: number | string;
  selectedTabStyle: StyleType;
  unselectedTabStyle: StyleType;
  gtV5?: boolean;
  gtLatest: boolean;
  newState?: any;
}

type extendedGTPayload = Omit<GroundTruth['payload'], 'scores'> & {
  scorecard: any;
  minimumScore: number;
  optionalSectionScoring: (number | null)[];
};

type StateWithGT = Record<string, any> & State;

export default class GroundTruthEditor extends Component<Props, StateWithGT> {
  constructor(props: Props) {
    super(props);
    const { groundTruth } = this.props;
    this.state = {
      minimumScore: 20,
      currentSection: 0,
      selectedTabStyle: {
        backgroundColor: '#66a3e5',
        color: 'white',
        borderBottom: '1px solid red',
      },
      unselectedTabStyle: {
        backgroundColor: 'lightgray',
        color: 'black',
        border: 'solid 2px white',
      },
      gtV5: groundTruth?.version === 5,
      gtLatest: !groundTruth || (groundTruth?.version !== undefined && groundTruth.version >= 6),
    };
  }

  componentDidMount(): void {
    const { groundTruth, survey } = this.props;
    if (groundTruth && !_.isEmpty(groundTruth.payload)) {
      this.restorePayload(groundTruth, survey);
    } else {
      this.resetForm();
    }
  }

  componentDidUpdate(prevProps: Props): void {
    const { groundTruth, survey } = this.props;
    if (prevProps.groundTruth !== groundTruth) {
      if (groundTruth && !_.isEmpty(groundTruth.payload)) {
        this.restorePayload(groundTruth, survey);
      } else {
        this.resetForm();
      }
    }
  }

  onSetScoreArbitraryString = (
    key: string,
    value: number,
    sectionId: number,
    questionIdx: string,
  ): void => {
    const fullKey = `${key}.${sectionId}.${questionIdx}`;
    this.setState({ [fullKey]: value });
  };

  getQuestionScores(
    question: SurveyQuestion,
    sectionIdx: number,
    sampleIdx: number,
    scorecard: any,
  ): void {
    switch (question.type) {
      case 'OneOf':
      case 'MultipleChoice': {
        const answers = question.options;
        if (!answers) {
          return;
        }
        for (let answerIdx = 0; answerIdx < answers.length; answerIdx += 1) {
          const uuid = `${question.question.uuid}.${answers?.[answerIdx].uuid}`;
          const elementScore = this.state[`${sectionIdx}.${sampleIdx}.${uuid}`];
          if (elementScore !== undefined) {
            scorecard.setScore(sectionIdx, sampleIdx, uuid, elementScore);
          } else {
            throw Error(`No score found for ${question.type}: ${sectionIdx}.${sampleIdx}.${uuid}.`);
          }
        }
        break;
      }
      case 'ArbitraryString':
        // nothing to do for this type
        break;
      case 'IntegerPercentage':
        // nothing to do for this type
        break;
      default:
        throw new Error(`Can not get scores for question of type: ${question.type} - ${JSON.stringify(question, null, 2)}`);
    }
  }

  getPayload(): extendedGTPayload {
    const { survey } = this.props;
    const {
      minimumScore,
    } = this.state;
    const scorecard = new Scorecard(survey.payload, { skipFreeText: true });
    for (let sectionIdx = 0, numSections = scorecard.getNumSections();
      sectionIdx < numSections;
      sectionIdx += 1) {
      for (let sampleIdx = 0, numSamples = scorecard.getNumCasesInSection(sectionIdx);
        sampleIdx < numSamples;
        sampleIdx += 1) {
        survey.payload.sections[sectionIdx].questions
          .forEach((question) => this.getQuestionScores(
            question, sectionIdx, sampleIdx, scorecard,
          ));
      }
    }

    const minimumScorePerQuestions = createArray(scorecard.getNumSections())
      .map((section, sectionId) => {
        const arr = createArray(scorecard.getNumQuestions(sectionId)).map((val, questionId) => this.state[`minimumScorePerQuestion.${sectionId}.${questionId}`]);
        return arr;
      });

    const maximumScorePerQuestion = createArray(scorecard.getNumSections())
      .map((section, sectionId) => {
        const arr = createArray(scorecard.getNumQuestions(sectionId)).map((val, questionId) => this.state[`maximumScorePerQuestion.${sectionId}.${questionId}`]);
        return arr;
      });

    const unity = createArray(scorecard.getNumSections())
      .map((section, sectionId) => {
        const arr = createArray(scorecard.getNumQuestions(sectionId))
          .map((val, questionId) => {
            const innerArr = createArray(scorecard.getNumCasesInSection(sectionId))
              .map((__, caseIndex) => this.state[`unity.${sectionId}.${questionId}.${caseIndex}`]);
            return innerArr;
          });
        return arr;
      });

    return {
      scorecard,
      minimumScore,
      evaluationFormulas:
        createArray(scorecard.getNumSections())
          .map((__, sectionId) => this.state[`evaluationFormula.${sectionId}`]),
      minimumSectionScores: createArray(scorecard.getNumSections())
        .map((__, sectionId) => this.state[`minimumScoreSection.${sectionId}`]),
      optionalSectionScoring: createArray(scorecard.getNumSections())
        .map((__, sectionId) => this.state[`optionalSectionScoring.${sectionId}`]),
      minimumScorePerQuestion: minimumScorePerQuestions,
      maximumScorePerQuestion,
      unity,
    };
  }

  restorePayload = (groundTruth: GroundTruth, survey: Survey): void => {
    const { payload, minimumScore, version } = groundTruth;
    if (version && version < 3) { // import old format: [sectionIdx].[sampleIdx][optionUuid]: score
      const scores = payload as any;
      const surveyPayload = survey.payload;
      surveyPayload.sections.forEach((surveySection, sectionIdx) => {
        surveySection.questions.forEach((question) => {
          switch (question.type) {
            case 'OneOf':
              question.options?.forEach((option) => {
                const newState: any = createArray(surveySection.numCases).reduce(
                  (p: any, __, sampleIdx) => {
                    const questionUuid = question.question.uuid;
                    const questionOptionUuid = `${questionUuid}.${option.uuid}`;
                    /*
                      before we ground truth was versioned
                      scores were referenced only by uuid of the answer,
                      in that case version is null
                    */
                    const scoreUuid = (version === null) ? option.uuid : questionOptionUuid;
                    const score = scores[sectionIdx][sampleIdx][scoreUuid];
                    return {
                      ...p,
                      [`${sectionIdx}.${sampleIdx}.${questionOptionUuid}`]: score,
                    };
                  },
                  {},
                );
                this.setState(newState);
              });
              break;
            case 'IntegerPercentage':
              // no GT for this question in old surveys
              break;
            default:
              throw new Error(`Unsupported question type: ${question.type}`);
          }
        });
      });
    } else { // import new format: [sectionIdx].[sampleIdx][questionUuid.optionUuid]: score
      const { scores, unity } = payload;
      scores.forEach((section, sectionIdx) => {
        section.forEach((sample, sampleId) => {
          Object.keys(sample).forEach((questionAnswerUuid) => {
            const score = sample[questionAnswerUuid];
            this.setState({
              [`${sectionIdx}.${sampleId}.${questionAnswerUuid}`]: score,
            });
          });
        });
      });

      if (unity) {
        unity.forEach((section, sectionIndex) => {
          section.forEach((question, questionIndex) => {
            question.forEach((sampleCase, sampleCaseIndex) => {
              this.setState({
                [`unity.${sectionIndex}.${questionIndex}.${sampleCaseIndex}`]: sampleCase,
              });
            });
          });
        });
      }
    }

    this.setState({ minimumScore });

    const formulas = (version && version < 3)
      ? []
      : payload.evaluationFormulas;
    formulas.forEach((formula, idx) => this.setState({ [`evaluationFormula.${idx}`]: formula }));

    const minimumSectionScores = (version && version > 3)
      ? payload.minimumSectionScores
      : [];
    minimumSectionScores.forEach((minScore, idx) => this.setState({ [`minimumScoreSection.${idx}`]: minScore }));

    if (payload.optionalSectionScoring
      && payload.optionalSectionScoring?.length > 0) {
      payload.optionalSectionScoring.map((value, index) => this.setState({
        [`optionalSectionScoring.${index}`]: value,
      }));
    }

    if (payload.maximumScorePerQuestion && payload.maximumScorePerQuestion.length) {
      payload.maximumScorePerQuestion.forEach((section, sectionIdx) => section.forEach(
        (questionScore, questionIdx) => this.setState({ [`maximumScorePerQuestion.${sectionIdx}.${questionIdx}`]: questionScore }),
      ));
    }

    if (payload.minimumScorePerQuestion && payload.minimumScorePerQuestion.length) {
      payload.minimumScorePerQuestion.forEach((section, sectionIdx) => section.forEach(
        (questionScore, questionIdx) => this.setState({ [`minimumScorePerQuestion.${sectionIdx}.${questionIdx}`]: questionScore }),
      ));
    }
  };

  resetFormQuestion = (sectionIdx: number, sampleIdx: number, question: SurveyQuestion): void => {
    switch (question.type) {
      case 'OneOf':
      case 'MultipleChoice':
        question.options?.forEach((option) => {
          this.setState({ [`${sectionIdx}.${sampleIdx}.${question.question.uuid}.${option.uuid}`]: 0 });
        });
        break;
      case 'ArbitraryString':
        // nothing to do for this type
        break;
      case 'IntegerPercentage':
        // nothing to do for this type as well
        break;
      default:
        throw new Error(`Unsupported question type for resetting form: ${JSON.stringify(question)}`);
    }
  };

  resetForm(): void {
    const { survey } = this.props;
    const { payload } = survey;
    for (let sectionIdx = 0, numSections = payload.sections.length;
      sectionIdx < numSections;
      sectionIdx += 1) {
      for (let sampleIdx = 0, numSamples = payload.sections[sectionIdx].numCases;
        sampleIdx < numSamples;
        sampleIdx += 1) {
        const { questions } = payload.sections[sectionIdx];
        questions.forEach((question) => this.resetFormQuestion(sectionIdx, sampleIdx, question));
      }
      this.setState({ [`evaluationFormula.${sectionIdx}`]: undefined });
      this.setState({ [`minimumScoreSection.${sectionIdx}`]: undefined });
      this.setState({ [`optionalSectionScoring.${sectionIdx}`]: null });
    }
    this.setState({ minimumScore: 20 });
  }

  renderSection = (): React.ReactNode => {
    const {
      aggregation,
      groundTruth,
      survey,
      disabled: disabledProp,
    } = this.props;
    const { currentSection, gtLatest } = this.state;
    const sectionId = currentSection as number;

    const hideMinimumScore = !gtLatest;

    const unity = groundTruth?.version !== undefined
      && groundTruth?.version >= 7 && groundTruth.payload
      && groundTruth.payload.unity
      ? groundTruth.payload.unity
      : undefined;

    const section = survey.payload.sections[sectionId];
    return (
      <div
        key={`GroundTruthSection.${sectionId}`}
      >
        {
          section.questions.map((question, questionIdx) => {
            const currMinScore = question.excludeFromMarking
              ? undefined
              // eslint-disable-next-line react/destructuring-assignment
              : parseInt(this.state[`minimumScorePerQuestion.${sectionId}.${questionIdx}`], 10);
            const currMaxScore = question.excludeFromMarking
              ? undefined
              // eslint-disable-next-line react/destructuring-assignment
              : parseInt(this.state[`maximumScorePerQuestion.${sectionId}.${questionIdx}`], 10);
            const currentUnityFlags = unity && unity[sectionId]
              ? unity[sectionId][questionIdx]
              : undefined;

            const disabled = disabledProp || question.excludeFromMarking;
            const mainKey = `GroundTruthEditor${question.type}.${sectionId}.${question.question.uuid}`;
            switch (question.type) {
              case 'OneOf':
                return (
                  <GroundTruthOneOf
                    key={mainKey}
                    numSamples={section.numCases}
                    collectSampleIds={false}
                    setScore={(key: string, value: number) => {
                      this.setState({ [key]: value });
                    }}
                    scores={this.state}
                    question={question.question}
                    questionId={questionIdx}
                    aggregation={aggregation}
                    sectionId={sectionId}
                    answers={{
                      oneOff: {
                        options: question.options,
                      },
                    }}
                    section={section}
                    currentMinimumScore={currMinScore}
                    currentMaximumScore={currMaxScore}
                    hideMinimumScore={hideMinimumScore}
                    disabled={disabled}
                  />
                );
              case 'MultipleChoice':
                return (
                  <GroundTruthMultipleChoice
                    key={mainKey}
                    numSamples={section.numCases}
                    collectSampleIds={false}
                    setScore={(key: string, value: number) => {
                      this.setState({ [key]: value });
                    }}
                    scores={this.state}
                    question={question.question}
                    questionId={questionIdx}
                    aggregation={aggregation}
                    sectionId={sectionId}
                    answers={{
                      oneOff: {
                        options: question.options,
                      },
                    }}
                    section={section}
                    currentMinimumScore={currMinScore}
                    currentMaximumScore={currMaxScore}
                    currentUnity={currentUnityFlags
                      || (!groundTruth
                        || (
                          groundTruth.version !== undefined
                          && groundTruth.version >= 7
                        ) ? [] : undefined)}
                    hideMinimumScore={hideMinimumScore}
                    disabled={disabled}
                  />
                );
              case 'ArbitraryString':
                return (
                  <GroundTruthArbitraryString
                    key={mainKey}
                    question={question.question.text as string}
                    setScore={this.onSetScoreArbitraryString}
                    sectionId={sectionId}
                    questionId={questionIdx}
                    currentMinimumScore={currMinScore}
                    currentMaximumScore={currMaxScore}
                    hideMinimumScore={hideMinimumScore}
                    disabled={disabled}
                  />
                );
              case 'IntegerPercentage':
                return false;
              default:
                return (
                  <div>
                    UNSUPPORTED TYPE
                    {question.type}
                  </div>
                );
            }
          })
        }
        <div className="ml2">
          {
            groundTruth?.version !== undefined
            && groundTruth?.version <= 5
            && section.questions.length > 1 && (
              <div>
                <Typography variant="h5">
                  <FormattedMessage id="groundtruthview.evaluationFormulaHeadline" />
                  {' '}
                  {sectionId + 1}
                </Typography>
                <TextField
                  style={{ width: '25%' }}
                  id={`GroundTruthEvaluationFormula.${sectionId}`}
                  label={<FormattedMessage id="groundtruthview.evaluationFormulaPlaceholder" />}
                  onChange={(e) => this.setState(
                    {
                      [`evaluationFormula.${sectionId}`]: e.target.value,
                    },
                  )}
                  value={this.state[`evaluationFormula.${sectionId}`] || ''}
                  disabled={disabledProp}
                  variant="standard"
                />
              </div>
            )
          }
          <Typography variant="h6">
            <FormattedMessage id="groundtruthview.minimumScoreSection" />
            {' '}
            {sectionId + 1}
          </Typography>
          <TextField
            style={{ width: '25%' }}
            id={`GroundTruth.${sectionId}.minimalScore`}
            label={<FormattedMessage id="groundtruthview.minimalScore" />}
            onChange={(e) => this.setState(
              {
                [`minimumScoreSection.${sectionId}`]: Number(e.target.value),
              },
            )}
            value={this.state[`minimumScoreSection.${sectionId}`] || 0}
            disabled={disabledProp}
            variant="standard"
          />
          <div>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={!!this.state[`optionalSectionScoring.${sectionId}`]}
                  onChange={(e) => this.setState({
                    [`optionalSectionScoring.${sectionId}`]: e.target.checked,
                  })}
                  color="primary"
                  disabled={!!groundTruth?.version && groundTruth?.version <= 5}
                />
              )}
              label={<FormattedMessage id="groundtruthview.considerOptionalSection" />}
            />
          </div>
        </div>
      </div>
    );
  };

  renderSettingsPage = (): React.ReactNode => {
    const {
      disabled,
      groundTruth,
      survey,
    } = this.props;
    const {
      minimumScore,
    } = this.state;
    const optionalSectionsEnabled = !!survey.optionalSections;
    return (
      <div>
        <hr />
        <div className="ml2">
          <Typography variant="h6">
            <FormattedMessage id="groundtruthview.minimalScore" />
          </Typography>
          {
            optionalSectionsEnabled
            && (
              <Typography color="secondary" variant="caption" component="div">
                <FormattedMessage id="groundtruthview.optionalSectionEnabled" />
              </Typography>
            )
          }
          <TextField
            style={{ width: '25%' }}
            id="GroundTruthMinimalScore"
            label={<FormattedMessage id="groundtruthview.minimalScore" />}
            onChange={(e) => this.setState({ minimumScore: Number(e.target.value) || 0 })}
            value={minimumScore}
            disabled={disabled}
            variant="standard"
          />
          <Typography variant="h6">
            Ground-truth Version
          </Typography>
          <TextField
            style={{ width: '25%' }}
            id="GroundTruthVersion"
            label="Ground-truth Version"
            value={groundTruth ? groundTruth.version : LATEST_GT_VERSION}
            disabled
            variant="standard"
          />
        </div>
      </div>
    );
  };

  renderTabs = (section: SurveySection, sectionIdx: number): React.ReactNode => {
    const key = `tab-section-${sectionIdx}`;
    return (
      <Tab
        key={key}
        label={section.headline || `Section ${sectionIdx + 1}`}
        onClick={() => this.setState({ currentSection: sectionIdx })}
        value={sectionIdx}
      />
    );
  };

  render(): JSX.Element {
    const { survey } = this.props;
    const {
      currentSection,
    } = this.state;
    if (!survey) {
      return <div>Props survey is missing</div>;
    }

    return (
      <div>
        <Tabs value={currentSection} indicatorColor="secondary" variant="scrollable" scrollButtons="auto">
          {
            survey.payload.sections.map(this.renderTabs)
          }
          <Tab
            key="settings-tab"
            label={<FormattedMessage id="groundtruthview.mainSettings" />}
            onClick={() => this.setState({ currentSection: 'settings' })}
            value="settings"
          />
        </Tabs>
        {currentSection === 'settings' ? this.renderSettingsPage() : this.renderSection()}
      </div>
    );
  }
}
